import React, { useCallback, useMemo, useRef, useState } from "react";
import "./Fota.scss";
import TablePage from "../../../components/patientsList/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../../common/types/tableCardType";
import { useQuery } from "@tanstack/react-query";
import { svgs } from "../../../common/assats";
import { GenericRecord } from "../../../common/types/filterObjType";
import FileUpload, {
  FileUploadRef,
} from "../../../components/FileUpload/FileUpload";
import Modal from "../../../components/Modal/Modal";
import {
  createFota,
  deleteFota,
  fetchAllFotas,
  publishFota,
} from "../../../common/api/fotaService";
import { DataFromServerType } from "../../../common/types/fotaType";
import DevicesModalTable from "../../../components/ModalTable/DevicesModalTable";
import {
  fetchAlldevices,
  fetchDevicesByFirmware,
} from "../../../common/api/deviceService";
import DevicesList from "../../../components/DevicesList/DevicesList";
import { DateRange } from "react-day-picker";
import Loader from "../../../components/Loader/Loader";
import { debounce } from "lodash";
import { saveFileInS3 } from "../../../common/function/s3Upload";

const sortKeyArray = [
  "firmwareVersionName",
  "firmwareVersionNumber",
  "uploadedFile",
  "firmwareVersionDate",
  "selectedDevices",
  "",
  "",
];

const Fota = () => {
  const { t } = useTranslation();

  // ====== Modal (FileUpload) States ======
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [footerActions, setFooterActions] = useState<React.ReactNode>(null);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalDescription, setModalDescription] = useState<string>("");
  const [modalContentClass, setModalContentClass] = useState<string>("");

  // ====== Modal (Publish Table) States ======
  const [isTableModalOpen, setTableModalOpen] = useState(false);
  const [tableModalTitle, setTableModalTitle] = useState<string>("");
  const [tableModalDescription, setTableModalDescription] =
    useState<string>("");
  const [tableModalContent, setTableModalContent] =
    useState<React.ReactNode>(null);
  const [tableModalFooterActions, setTableModalFooterActions] =
    useState<React.ReactNode>(null);
  const [tableModalContentClass, setTableModalContentClass] =
    useState<string>("");

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [filters, setFilters] = useState<string[]>([]);
  const [deleted, setDeleted] = useState(0);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    undefined
  );

  const selectedDevicesRef = useRef<string[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  const updateSelectedDevices: React.Dispatch<
    React.SetStateAction<string[]>
  > = (value) => {
    if (typeof value === "function") {
      // value is a function: (prev) => updated
      setSelectedDevices((prev) => {
        const newValue = (value as (prev: string[]) => string[])(prev);
        selectedDevicesRef.current = newValue;
        return newValue;
      });
    } else {
      // value is the actual array
      selectedDevicesRef.current = value;
      setSelectedDevices(value);
    }
  };
  
  const fileUploadRef = useRef<FileUploadRef>(null);
  const getText = (text: string) => t(`fota.${text}`);
  const cards: TableCard[] = React.useMemo(() => [], []);
  // ====== React Query: FotAs ======
  const {
    data: fotasList,
    isLoading: isLoadingFotas,
    refetch,
  } = useQuery({
    queryKey: [
      "fotasData",
      sortKey,
      sortOrder,
      debouncedSearch,
      filters,
      selectedRange,
      deleted,
    ],
    queryFn: () =>
      fetchAllFotas(
        sortKey,
        sortOrder,
        debouncedSearch,
        filters,
        selectedRange
      ),
  });

  // Debounce the search input
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300), // 300ms delay
    []
  );

  // ====== Handle Search ======
  // Update the search value when typing
  const handleSearchChange = (str: string) => {
    setSearch(str); // Instant feedback for the user
    debouncedSetSearch(str); // Delayed API request
  };
  // ====== Build Filters ======
  function getFilters(data: { id: string; SiteName: string }[]) {
    let filterObj: GenericRecord<string> = {
      center: data.map((val) => ({ name: val.SiteName, value: val.SiteName })),
      status: [
        { name: "Active", value: "1" },
        { name: "Inactive", value: "0" },
      ],
    };
    return filterObj;
  }
  function checkFotaUsed(fota: DataFromServerType) {
    return fota.installed;
  }
  // ====== Transform Table Data ======
  const transformFotaData = useCallback((data: DataFromServerType[] = []) => {
    return data.map((fota) => ({
      firmware_Version_Name: fota.firmwareVersionName,
      firmware_Version_Number: fota.firmwareVersionNumber,
      uploaded_File: fota.uploadedFile,
      firmware_Version_Date: fota.firmwareVersionDate,
      selected_Devices: (
        <button
          className="devices-link-button"
          onClick={() => handleDevicesModal(fota.firmwareVersionNumber)}
        >
          {"View Devices"}
        </button>
      ),
      publish: (
        <button
          className="publish-icon-button"
          onClick={() => {
            handlePublishModal(fota.firmwareVersionNumber, fota.id);
          }}
        >
          <svgs.fota.publish />
        </button>
      ),
      delete: checkFotaUsed(fota) ? (
        <></>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => {
            deleteFota(fota.id);
            setDeleted((prev) => prev + 1);
          }}
        >
          <svgs.icons.deleteIcone />
        </div>
      ),
    }));
  }, []);
  const handleDevicesModal = async (firmwareVersionNumber: string) => {
    try {
      setTableModalTitle("Devices List");
      setTableModalDescription(`Firmware version: ${firmwareVersionNumber}`);
      setTableModalContentClass("modal-table-page-container");

      // Fetch devices data from the API
      const devices = await fetchDevicesByFirmware(firmwareVersionNumber);
      
      setTableModalContent(
        <DevicesList
          devices={devices}
          firmwareVersion={firmwareVersionNumber}
          checkUpdated
        />
      );

      setTableModalOpen(true);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const tableData = React.useMemo(() => {
    if (!fotasList || isLoadingFotas) return [];
    return transformFotaData(fotasList);
  }, [fotasList, isLoadingFotas, transformFotaData]);

  // ====== Handle Publish Modal (Table) ======
  const handlePublishModal = (fotaNumber: string, fotaId: string) => {
    setTableModalTitle("Publish Version" + " " + fotaNumber);
    setTableModalDescription("Select Devices");
    setTableModalContentClass("modal-table-page-container");
    setTableModalFooterActions(
      <div className="modal-footer-actions">
        <button
          className="cancel-button"
          onClick={() => {
            setModalOpen(false);
            setTableModalOpen(false);
          }}
        >
          {t("Back")}
        </button>
        <button
          className="publish-button"
          onClick={() => {
            console.log("Publish firmware update to selected devices...");
            // handlePublish("all");
            setTableModalOpen(false);
            handleSuccessModal();
            const devicesToPublish = selectedDevicesRef.current;

            publishFota({ fotaId: fotaId, devicesIds: devicesToPublish });
          }}
        >
          {t("Publish")}
        </button>
      </div>
    );

    // const handlePublish = (selectedFilter: string) => {
    //   fotasList.forEach((fota: { selectedDevices: string }) => {
    //     fota.selectedDevices = selectedFilter;
    //   });
    //   setTableModalOpen(false);
    //   handleSuccessModal();
    // };

    setTableModalContent(
      <DevicesModalTable
        versionNumbers={fotasList || []}
        isLoading={isLoadingFotas}
        onClose={() => setTableModalOpen(false)}
        // onPublish={(devices: string[]) => updateFota(devices)}
        selectedDevices={selectedDevices}
        setOutsideSelectedDevices={updateSelectedDevices}
      />
    );

    setTableModalOpen(true);
  };
  async function uploadFile(e: any) {
    e.preventDefault();
    const firmwareName = fileUploadRef.current?.getFirmwareName() || "";
    const firmwareNumber = fileUploadRef.current?.getFirmwareNumber() || "";
    const file = fileUploadRef.current?.getFile();
    const fileName = file?.name || "";
    const fileType = file?.type || "";

    if (!firmwareName || !firmwareNumber || !file || !fileType) {
      return;
    }

    const nowDate = new Date().toISOString().split("T")[0];
    try {
      const response = await createFota({
        firmwareVersionName: firmwareName,
        firmwareVersionNumber: firmwareNumber,
        uploadedFile: fileName,
        firmwareVersionDate: nowDate,
        fileType: "application/octet-stream",
      });

      if (!response.success || !response.data.firmwareS3Url) {
        throw new Error("Failed to get signed URL");
      }
      const uploadRes = await saveFileInS3(file, response.data.firmwareS3Url);
      if (!uploadRes) {
        throw new Error("Upload to S3 failed");
      }

      refetch();
      setModalOpen(false);
    } catch (err) {
      console.error("Upload failed:", err);
    }
  }
  // ====== Handle File Upload Modal ======
  const handleFileUploadModal = () => {
    setModalContent(
      <FileUpload
        ref={fileUploadRef}
        onFilesUploaded={(files) => console.log("Files uploaded:", files)}
        fileType=".bin"
      />
    );

    setModalTitle(t("File Upload"));
    setModalDescription(t("Add your documents here"));
    setModalContentClass("modal-step-file-upload");

    setFooterActions(
      <div className="modal-footer-actions">
        <button className="cancel-button" onClick={() => setModalOpen(false)}>
          {t("Cancel")}
        </button>
        <button className="upload-button" onClick={uploadFile}>
          {t("Upload")}
        </button>
      </div>
    );

    setModalOpen(true);
  };

  const handleSuccessModal = () => {
    setTableModalTitle("");
    setTableModalDescription("");
    setTableModalContentClass("success-modal-container");
    setTableModalFooterActions(null);

    setTableModalContent(
      <div className="success-modal-content">
        <div className="success-icon">
          <svgs.fota.checked />
        </div>
        <p className="success-message">
          {getText("firmware_successfully_published")}
        </p>
      </div>
    );

    setTableModalOpen(true);
  };

  return (
    <>
      <TablePage
        searchAutoFocus
        customClass="fota-table-table-page-main-container"
        filterObj={fotasList ? getFilters(fotasList) : {}}
        tableHeadline={getText("fota")}
        headerHeadline={getText("fota")}
        cards={cards}
        trashAction={() => {}}
        setFilters={setFilters}
        searchValue={search}
        setSearchValue={handleSearchChange}
        searchPlaceholder={getText("Select_date")}
        buttonText={getText("Add_New")}
        buttonAction={handleFileUploadModal}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        tableData={tableData}
        sortKeysArray={sortKeyArray.map((value) => ({
          name: getText(`sort.${value}`),
          val: value,
        }))}
        // EndRowIcon={svgs.icons.deleteIcone}
        dateFilter={{ setSelectedRange, selectedRange }}
      />

      <Modal
        isOpen={isModalOpen}
        title={modalTitle}
        description={modalDescription}
        content={modalContent}
        onClose={() => setModalOpen(false)}
        footerActions={footerActions}
        contentClass={modalContentClass}
      />

      <Modal
        isOpen={isTableModalOpen}
        title={tableModalTitle}
        description={tableModalDescription}
        content={tableModalContent}
        onClose={() => setTableModalOpen(false)}
        footerActions={tableModalFooterActions}
        contentClass={tableModalContentClass}
      />
    </>
  );
};

export default Fota;
